<template lang="pug">
  CommonSection.b-spec-chats-page(title="Чаты" :back="{ title: project && project.name, name: 'project-stages', params: { projectId } }")
    SpecChatScreen(
      :chats="chats"
      :activeChat="activeChat"
      :messages="messages"
      :canCreate="!archived"
      :canWrite="!archived"
      itemsAreLinks
      requestClickable
      @select="openChat"
      @create="createChat"
      @send="send"
      @back="closeChat"
      @goto-item="gotoItem"
      @goto-request="gotoRequests"
      )
</template>

<script>
import CommonSection from '@/components/CommonSection.vue';
import SpecChatScreen from '@/components/spec-chat/SpecChatScreen.vue';
import SpecChatCreatePopupWrappable from '@/components/spec-chat/SpecChatCreatePopupWrappable.vue';
import popup from '@/model/popup';
import { apiRun } from '@/model/apiRun';
import model from '@/model/specification';
import specChat from '@/model/specChat';
import specificationApi from '@/api/specification';
import { idOf } from '@/utils/spec';

/**
 * Вкладка проекта «Чаты» (ТЗ 6.10, дизайнер): все чаты спецификации, лента выбранного чата,
 * создание трёхстороннего чата. Экран — общий компонент трёх ролей, страница только даёт ему
 * данные и права. Сгенерировано Claude.
 */
export default {
  name: 'SpecChatsPage',
  components: { CommonSection, SpecChatScreen },
  props: {
    project: Object,
  },
  data() {
    return {
      specification: null,
      chats: [],
      activeChat: null,
      messages: [],
    };
  },
  computed: {
    projectId() { return Number(this.$route.params.projectId); },
    routeChatId() { return Number(this.$route.params.chatId) || null; },
    archived() { return !!this.project?.isArchived; },
  },
  watch: {
    projectId: {
      immediate: true,
      handler() { this.load(); },
    },
    routeChatId(id) {
      if (id && this.activeChat?.id !== id) this.selectById(id);
    },
  },
  methods: {
    async load() {
      // идентификатор спецификации берём из хранилища: позиции для списка чатов не нужны
      this.specification = await model.ensure(this.projectId);
      await this.reloadChats();
      if (this.routeChatId) this.selectById(this.routeChatId);
    },
    async reloadChats() {
      this.chats = await specChat.fetchDesignerChats(this.specification.id);
    },
    selectById(id) {
      const chat = this.chats.find((c) => c.id === id);
      if (chat) this.openChat(chat, false);
    },
    async openChat(chat, navigate = true) {
      this.activeChat = chat;
      this.messages = await specChat.fetchMessages(chat.id);
      if (navigate && this.routeChatId !== chat.id) {
        this.$router.replace({
          name: 'project-chat',
          params: { projectId: this.projectId, chatId: chat.id },
        });
      }
    },
    /**
     * Возврат к списку на узких экранах: список и лента показываются поочерёдно (ТЗ 6.10).
     * Адрес теряет параметр чата: он означает «открыт конкретный чат» (ТЗ 6.1).
     */
    closeChat() {
      this.activeChat = null;
      this.messages = [];
      if (this.routeChatId) {
        this.$router.replace({ name: 'project-chats', params: { projectId: this.projectId } });
      }
    },
    async send({ text, files }) {
      if (!this.activeChat) return;
      const sent = await apiRun(
        () => specChat.sendMessage(this.activeChat.id, text, files),
        'Не удалось отправить сообщение',
      );
      if (!sent) return;
      this.messages = await specChat.fetchMessages(this.activeChat.id);
      await this.reloadChats();
    },
    /** Поставщики с запросом КП и без трёхстороннего чата (ТЗ 6.10). */
    async createChat() {
      const { items } = await specificationApi.fetchRequests(this.specification.id);
      // поставщик чата приходит ссылкой, а в списке запросов — объектом, поэтому сравниваем
      // по идентификатору, добытому из любой формы
      const existing = this.chats
        .filter((chat) => chat.kind === 'tripartite')
        .map((chat) => idOf(chat.supplier));
      const suppliers = (items ?? [])
        .map((request) => request.supplier)
        .filter((supplier) => supplier && !existing.includes(idOf(supplier)))
        .filter((supplier, index, list) => list.findIndex((s) => s.id === supplier.id) === index);

      const result = await popup.add({
        wrappable: true,
        component: SpecChatCreatePopupWrappable,
        params: { suppliers },
      });
      if (!result) return;

      const chat = await apiRun(
        () => specChat.createTripartite(
          this.specification.id,
          result.supplierId,
          result.text,
          result.files,
        ),
        'Не удалось создать чат',
      );
      if (!chat) return;
      await this.reloadChats();
      const created = this.chats.find((c) => c.id === chat.id);
      if (created) await this.openChat(created);
    },
    /**
     * Переход «к позиции» из события чата (ТЗ 6.10): спецификация открывается с раскрытой
     * позицией — тот же адрес с параметром `item`, что и у ссылки из уведомления (6.8).
     */
    gotoItem(item) {
      this.$router.push({
        name: 'project-specification',
        params: { projectId: this.projectId },
        query: item?.id ? { item: String(item.id) } : {},
      });
    },
    /** Переход к запросу КП из события: панель «Запросы» на вкладке спецификации (ТЗ 6.10). */
    gotoRequests() {
      this.$router.push({
        name: 'project-specification',
        params: { projectId: this.projectId },
        query: { requests: '1' },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.spec-chats-page {
  padding-bottom: 40px;
}
</style>
